import { LoadSpinner } from '@calizahq/react-components-v2'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { TicketCommentQueries } from '@/graphql/queries/ticketComment.queries'
import { useGraphql } from '@/hooks/useGraphql'
import { Status, TicketDetail } from '@/types/Ticket'
import { TicketComment } from '@/types/TicketComment'

import { CreateComment } from './CreateComment'
import { ParentComment } from './ParentComment'

type Props = {
  ticketData: TicketDetail
}
export const CommunicationSection: FC<Props> = ({ ticketData }) => {
  const [activeCommentToReply, setActiveCommentToReply] = useState<number>()
  const [scrollToElement, setScrollToElementId] = useState<number>()
  const navigate = useNavigate()

  const { hash, pathname } = useLocation()
  useEffect(() => {
    const elementId = hash.substring(1)
    const value = Number(elementId)
    if (elementId && elementId.length > 0 && !isNaN(value)) {
      setScrollToElementId(value)
    } else {
      setScrollToElementId(undefined)
    }
  }, [hash])

  const { data, isLoading, refetch } = useGraphql<{ ticketComments: null | TicketComment[] }>(
    TicketCommentQueries.QUERY_KEY,
    TicketCommentQueries.getTicketComments(),
    {
      filter: {
        ticketId: ticketData.id,
        ...(ticketData.parentTicketId && {
          createdAtAfter: ticketData.createdAt,
          ...(ticketData.closedAt && { createdAtBefore: ticketData.closedAt }),
        }),
      },
    },
  )

  const feedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ticketComments = data?.ticketComments ?? []
    if (!ticketComments.length) return
    const el = feedRef.current
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight
    })
  }, [data])

  const isTicketClosed = ticketData.internalTicketStatus === Status.CLOSED

  const handleCommentSectionClick = () => {
    setActiveCommentToReply(undefined)
    if (hash && hash.length > 0) {
      navigate(pathname)
    }
  }

  const comments = data?.ticketComments ?? []

  return (
    <div>
      <div
        className="my-4 flex max-h-[70vh] min-h-[70vh] w-full flex-col overflow-y-scroll overflow-x-hidden px-4"
        onClick={handleCommentSectionClick}
        ref={feedRef}
        role="presentation"
      >
        {isLoading ? (
          <div className="flex h-48 w-full items-center justify-center ">
            <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
          </div>
        ) : (
          <>
            {comments.map((comment) => (
              <ParentComment
                activeCommentToReply={comment.ticketId === ticketData.id ? activeCommentToReply : undefined}
                data={comment}
                isReadOnly={comment.ticketId !== ticketData.id}
                key={comment.id}
                scrollToElement={scrollToElement}
                setActiveCommentToReply={comment.ticketId === ticketData.id ? setActiveCommentToReply : () => {}}
                supportedReactions={comment.ticketId === ticketData.id ? ticketData.supportedReactions : null}
              />
            ))}
          </>
        )}
      </div>
      {!isTicketClosed && (
        <>
          <CreateComment
            activeCommentToReply={activeCommentToReply}
            refetchOnSuccess={refetch}
            setActiveCommentToReply={setActiveCommentToReply}
            ticketId={ticketData.id}
          />
        </>
      )}
    </div>
  )
}
